import { useState, useEffect, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, EyeIcon, TrashIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { chargeCalculationService, type ChargeCalculation } from '../services/chargeCalculationService';
import { listPayrollImportsByCompetence } from '../services/payrollImportsService';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import CurrencyInput from '@/components/shared/CurrencyInput/CurrencyInput';

// Helper
const formatCurrency = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function ChargeCalculationsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState<ChargeCalculation[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // New Calculation State
    const [newCompetence, setNewCompetence] = useState('');
    const [newRevenue, setNewRevenue] = useState(0);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await chargeCalculationService.getAll();
            setItems(data);
        } catch (error) {
            toast.error('Erro ao carregar encargos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCompetence) return;

        try {
            // Check if payroll exists for this competence
            const payrolls = await listPayrollImportsByCompetence(newCompetence);
            if (payrolls.length === 0) {
                toast.error(`Não existe folha de pagamento importada para a competência ${newCompetence}. Importe a folha primeiro.`);
                return;
            }

            // Automatically link the first payroll import found (usually there's one per month)
            const linkedPayrollId = payrolls[0].id;

            // Simply navigate to the creation/draft page (client-side only)
            // We pass the initial data via state
            setIsModalOpen(false);
            navigate('/expenses/charges/new', {
                state: {
                    competence: newCompetence,
                    monthlyRevenue: newRevenue,
                    payrollImportId: linkedPayrollId
                }
            });

        } catch (error) {
            toast.error('Erro ao verificar disponibilidade de folha');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Excluir este cálculo?')) return;
        try {
            await chargeCalculationService.delete(id);
            toast.success('Excluído');
            loadData();
        } catch (error) {
            toast.error('Erro ao excluir');
        }
    };

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo="/expenses"
                icon={<BanknotesIcon className="h-5 w-5" aria-hidden />}
                title="Encargos Sociais"
                description="Gestão de cálculos mensais de encargos patronais."
                actions={
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                        <PlusIcon className="mr-2 h-5 w-5" />
                        Novo Cálculo
                    </button>
                }
            />

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Competência</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Base Total</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Encargos</th>
                            <th className="relative px-6 py-3"><span className="sr-only">Ações</span></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {loading ? (
                            <tr><td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">Carregando...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">Nenhum cálculo registrado.</td></tr>
                        ) : (
                            items.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{item.competence}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatCurrency(item.totalBase)}</td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatCurrency(item.totalCharges)}</td>

                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                        <Link to={`/expenses/charges/${item.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4 inline-flex items-center">
                                            <EyeIcon className="h-4 w-4 mr-1" /> Detalhes
                                        </Link>
                                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900 inline-flex items-center">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Transition appear show={isModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        Novo Cálculo de Encargos
                                    </Dialog.Title>
                                    <form onSubmit={handleCreate} className="mt-4 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Competência (Mês/Ano)</label>
                                            <input
                                                type="month"
                                                required
                                                value={newCompetence}
                                                onChange={(e) => setNewCompetence(e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <CurrencyInput
                                                id="newRevenue"
                                                name="newRevenue"
                                                label="Faturamento Mensal (para CPRB)"
                                                value={newRevenue}
                                                onValueChange={(val, name, values) => setNewRevenue(values.float)}
                                            />
                                        </div>

                                        <div className="mt-6 flex justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                                            >
                                                Criar Rascunho
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}
